import { DEFAULT_SDK_URL, DEFAULT_SERVER_URL, IS_PRODUCTION_DOMAIN, DEFAULT_APP_JS_BRIDGE } from "./constants";
import {
  noop,
  normalizeExposureConfig,
  resolveElement,
  toSensorsExposureViewOption,
  useExposurePlugin,
} from "./exposure";
import { createDefaultProfileProperties, createDefaultPublicProperties, mergeCommonProperties } from "./properties";
import { loadSensorsSDK } from "./sdk-loader";
import type {
  CollectCallback,
  CollectConfig,
  CollectInstance,
  ExposureConfig,
  ExposureTarget,
  PublicProperty,
  RegisterExposureOptions,
  SensorsConfig,
  SensorsExposureInstance,
  SensorsSDK,
  SensorsTask,
} from "./types";

class Collect implements CollectInstance {
  title: string;
  private readyPromise: Promise<SensorsSDK> | null;
  private exposurePromise: Promise<SensorsExposureInstance> | null;
  private exposureConfig: ExposureConfig | null;
  private initOptions: CollectConfig;

  constructor() {
    this.title = "";
    this.readyPromise = null;
    this.exposurePromise = null;
    this.exposureConfig = null;
    this.initOptions = {};
  }

  /**
   * 初始化神策 SDK：先异步加载 sdk_url，再注册插件，最后调用 sensors.init。
   * 多次 init 会复用同一个 Promise，避免重复挂载神策脚本。
   */
  init(opts: CollectConfig = {}): Promise<void> {
    if (this.readyPromise) {
      return this.readyPromise.then(() => undefined).catch((error) => {
        this.readyPromise = null;
        this.logSDKError("init", error);
      });
    }

    this.initOptions = opts;
    this.readyPromise = loadSensorsSDK(opts.sdk_url || DEFAULT_SDK_URL).then((sensors) => {
      return this.initExposurePlugin(sensors, opts).then(() => {
        sensors.init(this.mergeConfig(opts));
        window.SensorsData = sensors;

        if (opts.name) {
          window[opts.name] = sensors;
        }

        this.registerDefaultProperties(sensors, opts);
        this.setDefaultUserProfile(sensors);

        return sensors;
      });
    }).catch((error) => {
      this.readyPromise = null;
      throw error;
    });

    return this.readyPromise.then(() => undefined).catch((error) => {
      this.logSDKError("init", error);
    });
  }

  /**
   * 发送自定义事件，保留旧 GatCollect.track(eventName, properties, callback) 用法。
   */
  track(eventName: string, params?: Record<string, any> | CollectCallback, callback?: CollectCallback): void {
    try {
      const realCallback = this.wrapCallback(this.getCallback(params, callback), "track callback");
      const properties = mergeCommonProperties(this.title, params);

      this.runSDKTask((sensors) => {
        sensors.track(eventName, properties, realCallback);
      }, "track");
    } catch (error) {
      this.logSDKError("track", error);
    }
  }

  /**
   * 发送页面浏览事件。内部用神策 quick("autoTrack") 采集 $pageview。
   */
  pageView(title: string, params?: Record<string, any> | CollectCallback, callback?: CollectCallback): void {
    try {
      this.title = title;
      const realCallback = this.wrapCallback(this.getCallback(params, callback), "pageView callback");
      const properties = mergeCommonProperties(this.title, params);

      properties.$title = title;
      this.runSDKTask((sensors) => {
        sensors.quick("autoTrack", properties);
        realCallback?.();
      }, "pageView");
    } catch (error) {
      this.logSDKError("pageView", error);
    }
  }

  /**
   * 按旧事件名发送按钮点击事件。
   */
  trackBtn(params?: Record<string, any>, callback?: CollectCallback): void {
    this.track("btn_click", params, callback);
  }

  /**
   * 注册曝光元素，参数形态对齐神策 addExposureView，并统一使用蛇形字段。
   */
  registerExposureElement(target: ExposureTarget, options?: RegisterExposureOptions): () => void {
    let element: Element | null = null;

    try {
      element = resolveElement(target);
    } catch (error) {
      this.logExposureError(error);
      return noop;
    }

    if (!element) {
      return noop;
    }

    let canceled = false;
    let registered = false;

    this.getExposureInstance().then((exposure) => {
      if (!canceled) {
        const exposureOption = toSensorsExposureViewOption(options, element, this.exposureConfig as ExposureConfig);

        this.wrapExposureListener(exposureOption);
        exposure.addExposureView(element, exposureOption);
        registered = true;
      }
    }).catch((error) => {
      this.logExposureError(error);
    });

    return () => {
      canceled = true;

      if (registered) {
        this.getExposureInstance().then((exposure) => {
          exposure.removeExposureView?.(element);
        }).catch((error) => {
          this.logExposureError(error);
        });
      }
    };
  }

  /**
   * 注册神策公共属性，支持静态值或同步函数。
   */
  registerPage(properties: Record<string, PublicProperty>): Promise<void> {
    return this.withSDK((sensors) => sensors.registerPage?.(properties), "registerPage");
  }

  /** 绑定登录 ID。 */
  login(loginId: string): Promise<void> {
    return this.withSDK((sensors) => sensors.login?.(loginId), "login");
  }

  /** 取消登录 ID 关联。 */
  logout(isChangeId?: boolean): Promise<void> {
    return this.withSDK((sensors) => sensors.logout?.(isChangeId), "logout");
  }

  /** 设置用户属性。 */
  setProfile(properties: Record<string, any>): Promise<void> {
    return this.withSDK((sensors) => sensors.setProfile?.(properties), "setProfile");
  }

  /** 设置仅首次生效的用户属性。 */
  setOnceProfile(properties: Record<string, any>): Promise<void> {
    return this.withSDK((sensors) => sensors.setOnceProfile?.(properties), "setOnceProfile");
  }

  /** 追加数组类型用户属性。 */
  appendProfile(properties: Record<string, any>): Promise<void> {
    return this.withSDK((sensors) => sensors.appendProfile?.(properties), "appendProfile");
  }

  /** 递增或递减数值型用户属性。 */
  incrementProfile(properties: Record<string, any> | string): Promise<void> {
    return this.withSDK((sensors) => sensors.incrementProfile?.(properties), "incrementProfile");
  }

  /** 删除指定用户属性。 */
  unsetProfile(property: string | string[]): Promise<void> {
    return this.withSDK((sensors) => sensors.unsetProfile?.(property), "unsetProfile");
  }

  /** 删除当前用户及其用户属性。 */
  deleteProfile(): Promise<void> {
    return this.withSDK((sensors) => sensors.deleteProfile?.(), "deleteProfile");
  }

  /** 设置物品元数据。 */
  setItem(itemType: string, itemId: string, properties?: Record<string, any>): Promise<void> {
    return this.withSDK((sensors) => sensors.setItem?.(itemType, itemId, properties), "setItem");
  }

  /** 删除物品元数据。 */
  deleteItem(itemType: string, itemId: string): Promise<void> {
    return this.withSDK((sensors) => sensors.deleteItem?.(itemType, itemId), "deleteItem");
  }

  /** 获取神策预置属性。 */
  getPresetProperties(): Promise<Record<string, any>> {
    return this.getSDK().then((sensors) => sensors.getPresetProperties?.() || {}).catch((error) => {
      this.logSDKError("getPresetProperties", error);
      return {};
    });
  }

  /** 获取原始神策 SDK 实例，会等待 SDK 初始化完成。 */
  getSensorsInstance(): Promise<SensorsSDK> {
    return this.getSDK();
  }

  /** 在神策 SDK ready 后执行官方 API 调用。 */
  withSensors<T = void>(task: SensorsTask<T>): Promise<T> {
    return this.getSDK().then((sensors) => task(sensors));
  }

  /**
   * 合并神策初始化配置。
   */
  private mergeConfig(opts: CollectConfig): SensorsConfig {
    const legacyOpts = opts as Record<string, any>;
    const heatmapEnabled = Boolean(
      opts.auto_track || opts.auto_heatmap || legacyOpts.autoTrack || legacyOpts.autoHeatmap,
    );
    const reservedKeys = [
      "auto_track",
      "autoTrack",
      "auto_heatmap",
      "autoHeatmap",
      "debug_mode",
      "debugMode",
      "name",
      "exposure_plugin_url",
      "exposure",
      "platform_type",
      "platform_name",
      "is_login",
      "current_account_type",
      "public_properties",
      "publicProperties",
    ];
    const debugMode = opts.debug_mode ?? legacyOpts.debugMode;
    const showLog = IS_PRODUCTION_DOMAIN
      ? false
      : opts.show_log ?? (typeof debugMode === "number" ? debugMode !== 0 : false);
    const config: SensorsConfig = {
      sdk_url: opts.sdk_url || DEFAULT_SDK_URL,
      server_url: opts.server_url || DEFAULT_SERVER_URL,
      is_track_single_page: opts.is_track_single_page ?? false,
      use_client_time: opts.use_client_time ?? true,
      send_type: opts.send_type || "image",
      show_log: showLog,
      app_js_bridge: opts.app_js_bridge || DEFAULT_APP_JS_BRIDGE,
      heatmap: {
        clickmap: heatmapEnabled ? "default" : "not_collect",
        scroll_notice_map: "not_collect",
        ...opts.heatmap,
      },
    };

    Object.keys(opts).forEach((key) => {
      if (reservedKeys.indexOf(key) === -1 && typeof config[key] === "undefined") {
        config[key] = opts[key];
      }
    });

    return config;
  }

  /**
   * 注册默认公共属性：
   * platform_type / platform_name 初始化时设置一次；
   * is_login / current_account_type 每次事件触发时动态读取。
   */
  private registerDefaultProperties(sensors: SensorsSDK, opts: CollectConfig): void {
    try {
      sensors.registerPage?.(createDefaultPublicProperties(opts));
    } catch (error) {
      this.logSDKError("register default public properties", error);
    }

    const publicProperties = opts.public_properties || (opts as Record<string, any>).publicProperties;

    if (publicProperties) {
      try {
        sensors.registerPage?.(publicProperties);
      } catch (error) {
        this.logSDKError("register custom public properties", error);
      }
    }
  }

  /**
   * 初始化时设置用户属性 member_id 和 device_id。
   */
  private setDefaultUserProfile(sensors: SensorsSDK): void {
    const profile = createDefaultProfileProperties();
    const { member_id, device_id } = profile;

    if (member_id) {
      // 会触发神策系统预置的“$SignUp” 事件, 会更改神策“login_id”、“$identity_login_id”、“distinct_id” 的值
      try {
        sensors.login?.(member_id);
      } catch (error) {
        this.logSDKError("default login", error);
      }
    }
    if (device_id) {
      // 会更改神策的 “anonymous_id”、“$identity_anonymous_id” 的值
      try {
        sensors.identify?.(device_id);
      } catch (error) {
        this.logSDKError("default identify", error);
      }
    }

    // 若有其他用户属性需要设置，调用 setProfile一一设置
    // if (Object.keys(profile).length > 0) {
    //   sensors.setProfile?.(profile);
    // }
  }

  private initExposurePlugin(sensors: SensorsSDK, opts: CollectConfig): Promise<void> {
    this.exposureConfig = this.resolveExposureConfig(opts.exposure);

    if (!this.exposureConfig) {
      return Promise.resolve();
    }

    if (!this.exposurePromise) {
      this.exposurePromise = useExposurePlugin(sensors, this.exposureConfig, opts.exposure_plugin_url);
      return this.exposurePromise.then(() => undefined).catch((error) => {
        this.exposurePromise = null;
        this.logExposureError(error);
      });
    }

    return this.exposurePromise.then(() => undefined).catch((error) => {
      this.exposurePromise = null;
      this.logExposureError(error);
    });
  }

  /**
   * 获取 SDK 实例。若事件早于 init 调用，会使用默认配置自动初始化。
   */
  private getSDK(): Promise<SensorsSDK> {
    if (!this.readyPromise) {
      this.init(this.initOptions);
    }

    return this.readyPromise as Promise<SensorsSDK>;
  }

  private getExposureInstance(): Promise<SensorsExposureInstance> {
    return this.getSDK().then(() => {
      if (this.exposurePromise) {
        return this.exposurePromise;
      }

      return Promise.reject(new Error(
        "GatCollect Exposure is disabled. Please initialize with GatCollect.init({ exposure: true }) or GatCollect.init({ exposure: { area_rate?: number, stay_duration?: number, repeated?: boolean } }).",
      ));
    });
  }

  private resolveExposureConfig(exposure: CollectConfig["exposure"]): ExposureConfig | null {
    if (exposure === true) {
      return normalizeExposureConfig();
    }

    if (typeof exposure === "object" && exposure) {
      return normalizeExposureConfig(exposure);
    }

    return null;
  }

  /**
   * 在 SDK ready 后执行任务。
   */
  private runSDKTask(task: (sensors: SensorsSDK) => void, context: string): void {
    this.withSDK(task, context).catch(noop);
  }

  private withSDK(task: (sensors: SensorsSDK) => void, context: string): Promise<void> {
    return this.getSDK().then((sensors) => {
      task(sensors);
    }).catch((error) => {
      this.logSDKError(context, error);
    });
  }

  private getCallback(params?: Record<string, any> | CollectCallback, callback?: CollectCallback): CollectCallback | undefined {
    return typeof params === "function" ? (params as CollectCallback) : callback;
  }

  private wrapCallback(callback: CollectCallback | undefined, context: string): CollectCallback | undefined {
    if (typeof callback !== "function") {
      return undefined;
    }

    return (...args: any[]) => {
      try {
        callback(...args);
      } catch (error) {
        this.logSDKError(context, error);
      }
    };
  }

  private wrapExposureListener(exposureOption: Record<string, any>): void {
    const listener = exposureOption.listener || {};
    const shouldExpose = listener.shouldExpose;
    const didExpose = listener.didExpose;
    const hasCustomTitle = Boolean(
      exposureOption.properties
        && Object.prototype.hasOwnProperty.call(exposureOption.properties, "$title"),
    );

    exposureOption.listener = {
      ...listener,
      shouldExpose: (element: Element, properties: Record<string, any>) => {
        try {
          if (!hasCustomTitle) {
            properties.$title = this.title || document.title;
          }

          if (!Object.prototype.hasOwnProperty.call(properties, "$url")) {
            properties.$url = window.location.href;
          }

          if (typeof shouldExpose === "function") {
            return shouldExpose(element, properties);
          }

          return true;
        } catch (error) {
          this.logExposureError(error);
          return false;
        }
      },
      didExpose: typeof didExpose === "function"
        ? (element: Element, properties: Record<string, any>) => {
            try {
              didExpose(element, properties);
            } catch (error) {
              this.logExposureError(error);
            }
          }
        : didExpose,
    };
  }

  private logSDKError(context: string, error: unknown): void {
    if (typeof console !== "undefined" && console.error) {
      console.error(`[GatCollect] ${context} error:`, error);
    }
  }

  private logExposureError(error: unknown): void {
    if (typeof console !== "undefined" && console.error) {
      console.error("[GatCollect] Sensors Exposure error:", error);
    }
  }
}

const GatCollect = new Collect();

if (typeof window !== "undefined") {
  window.GatCollect = GatCollect;
}

export type {
  CollectCallback,
  CollectConfig,
  CollectInstance,
  ExposureConfig,
  ExposureTarget,
  PublicProperty,
  RegisterExposureOptions,
  SensorsExposureInstance,
  SensorsSDK,
  SensorsTask,
};
export default GatCollect;
